import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Camera, MapPin, Trash2, Image, Loader2, X } from "lucide-react";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface InstallationPhotosProps {
  installationId: string;
  readonly?: boolean;
}

interface InstallationPhoto {
  id: string;
  installation_id: string;
  photo_type: "before" | "after";
  file_path: string;
  file_name: string;
  file_size: number;
  latitude: number | null;
  longitude: number | null;
  captured_at: string;
  notes: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export function InstallationPhotos({ installationId, readonly = false }: InstallationPhotosProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState<"before" | "after">("before");
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState("");
  const [previewPhoto, setPreviewPhoto] = useState<InstallationPhoto | null>(null);

  const { data: photos, isLoading } = useQuery({
    queryKey: ["installation-photos", installationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("installation_photos")
        .select("*")
        .eq("installation_id", installationId)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as InstallationPhoto[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (photo: InstallationPhoto) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("installation-photos")
        .remove([photo.file_path]);

      if (storageError) console.error("Storage delete error:", storageError);

      // Soft delete from database
      const { error: dbError } = await supabase
        .from("installation_photos")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", photo.id);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installation-photos", installationId] });
      toast.success("Kuva poistettu");
    },
    onError: (error) => {
      console.error("Delete error:", error);
      toast.error("Virhe kuvan poistossa");
    },
  });

  const getCurrentPosition = (): Promise<GeolocationPosition | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Vain kuvatiedostot sallittu");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error("Tiedosto liian suuri (max 20MB)");
      return;
    }

    setUploading(true);

    try {
      // Get GPS coordinates
      const position = await getCurrentPosition();
      const latitude = position?.coords.latitude || null;
      const longitude = position?.coords.longitude || null;

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Generate unique file path
      const fileExt = file.name.split(".").pop();
      const fileName = `${installationId}/${uploadType}_${Date.now()}.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("installation-photos")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Save to database
      const { error: dbError } = await supabase
        .from("installation_photos")
        .insert({
          installation_id: installationId,
          photo_type: uploadType,
          file_path: fileName,
          file_name: file.name,
          file_size: file.size,
          latitude,
          longitude,
          notes: notes || null,
          uploaded_by: user?.id || null,
          captured_at: new Date().toISOString(),
        });

      if (dbError) throw dbError;

      queryClient.invalidateQueries({ queryKey: ["installation-photos", installationId] });
      toast.success("Kuva ladattu", { 
        description: latitude && longitude ? "GPS-sijainti tallennettu" : "Sijaintia ei saatavilla" 
      });
      setNotes("");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Virhe kuvan latauksessa");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const openCamera = (type: "before" | "after") => {
    setUploadType(type);
    fileInputRef.current?.click();
  };

  const getPhotoUrl = async (filePath: string): Promise<string | null> => {
    const { data } = await supabase.storage
      .from("installation-photos")
      .createSignedUrl(filePath, 3600);
    return data?.signedUrl || null;
  };

  const openPreview = async (photo: InstallationPhoto) => {
    setPreviewPhoto(photo);
  };

  const beforePhotos = photos?.filter((p) => p.photo_type === "before") || [];
  const afterPhotos = photos?.filter((p) => p.photo_type === "after") || [];

  return (
    <div className="space-y-4">
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {!readonly && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Lisää kuva</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              placeholder="Muistiinpanot (valinnainen)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
            <div className="flex gap-2">
              <Button
                onClick={() => openCamera("before")}
                disabled={uploading}
                variant="outline"
                className="flex-1"
              >
                {uploading && uploadType === "before" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4 mr-2" />
                )}
                Ennen-kuva
              </Button>
              <Button
                onClick={() => openCamera("after")}
                disabled={uploading}
                variant="outline"
                className="flex-1"
              >
                {uploading && uploadType === "after" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4 mr-2" />
                )}
                Jälkeen-kuva
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <PhotoSection
            title="Ennen"
            photos={beforePhotos}
            onPreview={openPreview}
            onDelete={readonly ? undefined : (photo) => deleteMutation.mutate(photo)}
            getPhotoUrl={getPhotoUrl}
          />
          <PhotoSection
            title="Jälkeen"
            photos={afterPhotos}
            onPreview={openPreview}
            onDelete={readonly ? undefined : (photo) => deleteMutation.mutate(photo)}
            getPhotoUrl={getPhotoUrl}
          />
        </div>
      )}

      <Dialog open={!!previewPhoto} onOpenChange={() => setPreviewPhoto(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewPhoto?.photo_type === "before" ? "Ennen" : "Jälkeen"}
              {previewPhoto?.latitude && previewPhoto?.longitude && (
                <Badge variant="outline" className="font-normal">
                  <MapPin className="h-3 w-3 mr-1" />
                  GPS
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {previewPhoto && (
            <PhotoPreviewContent photo={previewPhoto} getPhotoUrl={getPhotoUrl} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoSection({
  title,
  photos,
  onPreview,
  onDelete,
  getPhotoUrl,
}: {
  title: string;
  photos: InstallationPhoto[];
  onPreview: (photo: InstallationPhoto) => void;
  onDelete?: (photo: InstallationPhoto) => void;
  getPhotoUrl: (filePath: string) => Promise<string | null>;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Image className="h-4 w-4" />
          {title} ({photos.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {photos.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Ei kuvia
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {photos.map((photo) => (
              <PhotoThumbnail
                key={photo.id}
                photo={photo}
                onPreview={onPreview}
                onDelete={onDelete}
                getPhotoUrl={getPhotoUrl}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PhotoThumbnail({
  photo,
  onPreview,
  onDelete,
  getPhotoUrl,
}: {
  photo: InstallationPhoto;
  onPreview: (photo: InstallationPhoto) => void;
  onDelete?: (photo: InstallationPhoto) => void;
  getPhotoUrl: (filePath: string) => Promise<string | null>;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPhotoUrl(photo.file_path).then((url) => {
      setImageUrl(url);
      setLoading(false);
    });
  }, [photo.file_path]);

  return (
    <div className="relative group aspect-square rounded-md overflow-hidden bg-muted">
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={photo.file_name}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => onPreview(photo)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Image className="h-6 w-6 text-muted-foreground" />
        </div>
      )}

      {photo.latitude && photo.longitude && (
        <Badge
          variant="secondary"
          className="absolute top-1 left-1 text-xs px-1 py-0"
        >
          <MapPin className="h-3 w-3" />
        </Badge>
      )}

      {onDelete && (
        <Button
          size="icon"
          variant="destructive"
          className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(photo);
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

function PhotoPreviewContent({
  photo,
  getPhotoUrl,
}: {
  photo: InstallationPhoto;
  getPhotoUrl: (filePath: string) => Promise<string | null>;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPhotoUrl(photo.file_path).then((url) => {
      setImageUrl(url);
      setLoading(false);
    });
  }, [photo.file_path]);

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={photo.file_name}
          className="w-full max-h-[60vh] object-contain rounded-md"
        />
      ) : (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          Kuvaa ei voitu ladata
        </div>
      )}

      <div className="text-sm text-muted-foreground space-y-1">
        <p>
          <strong>Otettu:</strong>{" "}
          {format(new Date(photo.captured_at), "d.M.yyyy 'klo' HH:mm", { locale: fi })}
        </p>
        {photo.latitude && photo.longitude && (
          <p>
            <strong>Sijainti:</strong>{" "}
            <a
              href={`https://www.google.com/maps?q=${photo.latitude},${photo.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {photo.latitude.toFixed(6)}, {photo.longitude.toFixed(6)}
            </a>
          </p>
        )}
        {photo.notes && (
          <p>
            <strong>Muistiinpanot:</strong> {photo.notes}
          </p>
        )}
      </div>
    </div>
  );
}
